import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import SyncIcon from '@mui/icons-material/Sync';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { BillingMigrationSessionResponse } from '@store/generated/billing-migration';

import { useListBillingMigrationSessionsQuery } from '../store/billing-migration-api';
import {
  type BillingMigrationImportStats,
  getPollingInterval,
  getStatusColor,
  getStatusLabel,
  isSessionProcessing,
} from '../utils/billing-migration-status-helpers';

interface BillingMigrationSessionsListProps {
  onRefresh?: boolean;
}

const getStatusIcon = (status: string) => {
  switch (status.toLowerCase()) {
    case 'completed':
      return <CheckCircleIcon fontSize="small" />;
    case 'failed':
      return <ErrorIcon fontSize="small" />;
    case 'parsing':
    case 'importing':
      return <SyncIcon fontSize="small" sx={{ animation: 'spin 1s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />;
    case 'preview_ready':
      return <HourglassEmptyIcon fontSize="small" />;
    default:
      return undefined;
  }
};

const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const getRelativeTime = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '';
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
};

export default function BillingMigrationSessionsList({ onRefresh }: BillingMigrationSessionsListProps) {
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useListBillingMigrationSessionsQuery();
  const [, setTick] = useState(0);

  // Re-fetch when parent toggles onRefresh
  useEffect(() => {
    void refetch();
  }, [onRefresh, refetch]);

  // Dynamic polling based on session statuses
  const sessions = useMemo(() => data?.sessions ?? [], [data]);

  useEffect(() => {
    const intervals = sessions
      .map(s => getPollingInterval(s.status))
      .filter((v): v is number => v !== false);

    if (intervals.length === 0) return;

    const shortest = Math.min(...intervals);
    const id = setInterval(() => {
      void refetch();
      setTick(t => t + 1);
    }, shortest);

    return () => clearInterval(id);
  }, [sessions, refetch]);

  const handleRowClick = useCallback(
    (sessionId: string) => {
      navigate(`/practice/billing-data-migration/session/${sessionId}`);
    },
    [navigate],
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
        <CircularProgress size={24} sx={{ mr: 1 }} />
        <Typography color="text.secondary">Loading sessions...</Typography>
      </Box>
    );
  }

  if (isError) {
    return <Alert severity="error">Failed to load billing migration sessions.</Alert>;
  }

  if (sessions.length === 0) {
    return (
      <Card variant="outlined">
        <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
          <UploadFileIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography color="text.secondary">No billing migration sessions yet.</Typography>
          <Typography variant="caption" color="text.secondary">
            Upload X12 files to get started.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell>
            <TableCell>Source System</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Files</TableCell>
            <TableCell align="right">Claims</TableCell>
            <TableCell align="right">ERAs</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sessions.map((session: BillingMigrationSessionResponse) => {
            const stats = session.import_stats as BillingMigrationImportStats | undefined;
            const processing = isSessionProcessing(session.status);

            return (
              <TableRow
                key={session.session_id}
                hover
                sx={{ cursor: 'pointer', '& td': { opacity: processing ? 0.8 : 1 } }}
                onClick={() => handleRowClick(session.session_id)}
              >
                <TableCell>
                  <Typography variant="body2">{formatDate(session.created_at)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {getRelativeTime(session.created_at)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                    {session.source_system}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={getStatusLabel(session.status)}
                    color={getStatusColor(session.status)}
                    icon={getStatusIcon(session.status)}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">
                    {stats?.files_total ?? '—'}
                    {stats?.files_837 != null && stats?.files_835 != null && (
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                        ({stats.files_837} / {stats.files_835})
                      </Typography>
                    )}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">
                    {stats?.claims_created ?? stats?.total_claims ?? '—'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">
                    {stats?.remittances_created ?? stats?.total_remittances ?? '—'}
                  </Typography>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
